// components/chat/FileMessageBubble.jsx
import React, { useState } from 'react';
import fileService from '../../services/fileService';
import encryption from '../../services/encryption';
import { useNotification } from '../../hooks/useNotification';

export function FileMessageBubble({ fileInfo, isOwn }) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const { showNotification } = useNotification();

  const handleDownload = async () => {
    setIsDownloading(true);
    setDownloadProgress(0);

    try {
      const privateKey = encryption.getPrivateKey();
      if (!privateKey) {
        showNotification('Please load your private key first', 'warning');
        return;
      }

      // Download encrypted file
      const { blob, metadata } = await fileService.downloadFile(
        fileInfo.fileId,
        (progress) => setDownloadProgress(progress.progress)
      );

      // Decrypt file
      const decryptedBlob = await fileService.decryptFile(blob, metadata, privateKey);
      
      // Save file
      fileService.saveFile(decryptedBlob, fileInfo.fileName);
      
      showNotification(`File "${fileInfo.fileName}" downloaded`, 'success');
      
    } catch (error) {
      console.error('Download error:', error);
      showNotification(`Download failed: ${error.message}`, 'error');
    } finally {
      setIsDownloading(false);
      setDownloadProgress(0);
    }
  };

  const fileIcon = fileService.getFileIcon(fileInfo.mimeType);
  const fileSize = fileService.formatFileSize(fileInfo.fileSize);

  return (
    <div className="flex flex-col">
      <div 
        className={`p-3 rounded-2xl shadow cursor-pointer hover:opacity-90 transition ${
          isOwn 
            ? 'bg-blue-500 text-white' 
            : 'bg-white text-gray-800'
        }`}
        onClick={!isDownloading ? handleDownload : undefined}
      >
        <div className="flex items-center space-x-3">
          <span className="text-2xl">{fileIcon}</span>
          <div className="flex-1 min-w-0">
            <p className="font-medium truncate">{fileInfo.fileName}</p>
            <p className={`text-xs ${isOwn ? 'text-blue-100' : 'text-gray-500'}`}>
              {fileSize}
            </p>
          </div>
          
          {isDownloading ? (
            <div className="flex flex-col items-center">
              <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="text-xs">{Math.round(downloadProgress)}%</span>
            </div>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 ${isOwn ? 'text-white' : 'text-gray-500'}`} viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          )}
        </div>
        
        {isDownloading && downloadProgress > 0 && (
          <div className="mt-2 w-full bg-gray-200 rounded-full h-1">
            <div
              className={`h-1 rounded-full transition-all duration-300 ${
                isOwn ? 'bg-white' : 'bg-blue-500'
              }`}
              style={{ width: `${downloadProgress}%` }}
            />
          </div>
        )}
      </div>
    </div>
  );
}